import { ChangeEvent, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X } from 'lucide-react';
import { compressAndConvertToBase64, dataUrlToFile } from '@/utils/imageProcessor';

interface ImageUploadProps {
  value: File[];
  onChange: (files: File[]) => void;
  onBase64Change?: (images: string[]) => void;
  maxFiles?: number;
  accept?: string;
}

export function ImageUpload({
  value = [],
  onChange,
  onBase64Change,
  maxFiles = 5,
  accept = 'image/*',
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Don't exceed max files
    const filesToProcess = files.slice(0, maxFiles - value.length);
    
    setIsProcessing(true);
    
    try {
      const processedFiles: File[] = [];
      const processedBase64: string[] = [];
      const newPreviews: string[] = [...previews];
      
      // Process each file
      for (const file of filesToProcess) {
        // Create a temporary preview
        const tempPreview = URL.createObjectURL(file);
        newPreviews.push(tempPreview);
        
        // Process the image
        const processedImageData = await compressAndConvertToBase64(file);
        
        // Convert back to File
        const processedFile = dataUrlToFile(
          processedImageData,
          `${file.name.split('.')[0]}.png`
        );
        
        processedFiles.push(processedFile);
        processedBase64.push(processedImageData);
        
        // Revoke the temporary preview URL
        URL.revokeObjectURL(tempPreview);
      }
      
      // Update the previews with the processed images
      setPreviews([...previews, ...processedBase64]);
      
      // Update files
      const newFiles = [...value, ...processedFiles];
      onChange(newFiles);
      
      // Call base64 change callback if provided
      if (onBase64Change) {
        // Get base64 data for all files
        const allBase64 = [...processedBase64];
        onBase64Change(allBase64);
      }
    } catch (error) {
      console.error('Error processing images:', error);
      // Fallback to original files
      const newFiles = [...value, ...filesToProcess].slice(0, maxFiles);
      onChange(newFiles);
    } finally {
      setIsProcessing(false);
    }
    
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newFiles = value.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    onChange(newFiles);
    setPreviews(newPreviews);
    
    if (onBase64Change) {
      onBase64Change(newPreviews);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        {value.map((file, index) => (
          <div
            key={index}
            className="relative aspect-square h-32 w-32 rounded-lg border bg-gray-50"
          >
            <img
              src={previews[index] || URL.createObjectURL(file)}
              alt={`Preview ${index + 1}`}
              className="h-32 w-32 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {value.length < maxFiles && (
          <Button
            type="button"
            variant="outline"
            className="aspect-square h-32"
            onClick={() => inputRef.current?.click()}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="animate-pulse">Processing...</div>
            ) : (
              <ImagePlus className="w-10 h-10" />
            )}
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      
      <p className="text-xs text-muted-foreground">
        Upload up to {maxFiles} image(s). Supported formats: PNG, JPG, JPEG.
      </p>
    </div>
  );
} 